import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

type Account = { address: string } | null;

@Injectable({ providedIn: 'root' })
export class WalletConnectService {
  private appKit: any | null = null;
  private account$ = new BehaviorSubject<Account>(null);
  private platformBrowser = false;

  private includedWalletIds: string[] | undefined;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.platformBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Set custom wallets dynamically
   */
  setCustomWallets(ids?: string[]) {
    this.includedWalletIds = ids && ids.length ? ids.filter(Boolean) : undefined;
    console.log('[Wallet IDs Set]', this.includedWalletIds || 'No IDs passed, using wallet explorer');
  }

  /**
   * Initialize AppKit using the **current wallet IDs**
   */
  private async initialize(): Promise<void> {
    if (!this.platformBrowser) return;

    // Load sequentially (not Promise.all) — these packages reference each
    // other internally, and loading their chunks concurrently can race,
    // resolving an adapter's exports before its own dependency chunk
    // has finished registering.
    const { createAppKit } = await import('@reown/appkit');
    const { EthersAdapter } = await import('@reown/appkit-adapter-ethers');
    const { mainnet, polygon } = await import('@reown/appkit/networks');

    const networks = [mainnet, polygon] as [any, ...any[]];
    const ethersAdapter = new EthersAdapter();

    try {
      this.appKit = createAppKit({
        adapters: [ethersAdapter],
        networks,
        projectId: 'b31bd42ce992241e1d74e18a5ed48387',

        metadata: {
          name: 'Qliktag',
          description: 'NFT Verification',
          url: window.location.origin,
          icons: ['https://assets.reown.com/reown-profile-pic.png']
        },

        themeVariables: { '--apkt-z-index': 2147483647 },

        // If includedWalletIds is undefined, AppKit shows full wallet explorer
        includeWalletIds: this.includedWalletIds,

        // Optionally highlight a featured wallet
        featuredWalletIds: this.includedWalletIds && this.includedWalletIds.length
          ? [this.includedWalletIds[0]]
          : undefined,

        enableInjected: true,

        features: {
          analytics: false,
          email: false,
          socials: false
        }
      });

      this.appKit.subscribeAccount((account: any) => {
        this.account$.next(account?.address ? { address: account.address } : null);
      });

      console.log('[AppKit Initialized With]', this.includedWalletIds || 'Full wallet explorer');
    } catch (err) {
      console.error('[WalletConnect] createAppKit() failed:', err);
      throw err;
    }
  }

  /**
   * Connect wallet — optionally pass wallet IDs dynamically
   */
  async connect(walletIds?: string[]): Promise<void> {
    this.setCustomWallets(walletIds);

    // Always re-initialize right before opening
    this.appKit = null;
    await this.initialize();

    await this.appKit?.open({ view: 'Connect' });
  }

  async disconnect(): Promise<void> {
    await this.appKit?.disconnect();
  }

  getAccount(): Account {
    return this.account$.value;
  }

  onAccountChange(cb: (account: Account) => void) {
    return this.account$.subscribe(cb);
  }
}